import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Check, Store, X } from 'lucide-react'
import {
  Combobox,
  ComboboxContent,
  ComboboxTrigger,
} from 'tailwind-v4/components/popover-combobox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'tailwind-v4/components/command'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'tailwind-v4/components/tooltip'
import { Separator } from 'tailwind-v4/components/separator'
import { useVenues } from '@/hooks/useVenue'
import { useFeatureFlags } from '@/hooks/use-feature-flags'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import {
  SquareLocationsFetcher,
  useAllSquareLocations,
} from '@/hooks/use-all-square-locations'
import { SwitcherChevron } from './switcher-chevron'
import { findItemChainForPath, findSectionForPath, resolveHref } from './config'

function VenueInitial({ name }: { name: string }) {
  const initial = (name?.[0] ?? '?').toUpperCase()
  return (
    <span
      aria-hidden
      className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-[var(--accent)] text-[10px] font-semibold text-[var(--accent-foreground)]"
    >
      {initial}
    </span>
  )
}

export function VenueSwitcher({
  /**
   * On mobile, show only the venue's initials (first letter of each word)
   * instead of the full name. Opt-in — used by the top-bar picker where space
   * is tight; the mobile-menu pills keep the full (truncated) name.
   */
  initialsOnMobile = false,
}: {
  initialsOnMobile?: boolean
} = {}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const organisation = useSelectedOrganisation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [venues] = useVenues()
  const { newUi } = useFeatureFlags()

  const orgId = organisation?.id ?? ''

  // All venue-scoped URLs live under `/:orgId/venues/:serial/...`, so the
  // selected venue is always at segment 2 behind `venues` — or absent.
  const pathSegments = pathname.split('/').filter(Boolean)
  const serial =
    pathSegments[1] === 'venues' &&
    typeof pathSegments[2] === 'string' &&
    pathSegments[2].length > 0
      ? pathSegments[2]
      : undefined

  // Context-aware filter: on Square pages, narrow the list to venues mapped
  // to a Square location. Works for both the legacy `/<orgId>/square/...`
  // shape and the canonical `/<orgId>/venues/:serial/square/...` shape.
  const isSquareContext =
    Boolean(orgId) &&
    (pathname.startsWith(`/${orgId}/square`) ||
      /\/venues\/[^/]+\/square(\/|$)/.test(pathname))

  const {
    integrations: squareIntegrations,
    mappedSerials: squareMappedSerials,
    report: reportSquareLocations,
  } = useAllSquareLocations(orgId, !isSquareContext)

  const scopedVenues = useMemo(() => {
    if (!isSquareContext) return venues
    // Until Square mappings have loaded, don't filter — otherwise the
    // switcher flashes "All venues" while the fetch is in flight. Once
    // mappings arrive, the filter kicks in and narrows the list.
    if (squareMappedSerials.size === 0) return venues
    return venues.filter(
      (v: any) => v?.serial && squareMappedSerials.has(v.serial)
    )
  }, [venues, isSquareContext, squareMappedSerials])

  const currentVenue = useMemo(
    () => scopedVenues.find((v: any) => v?.serial === serial),
    [scopedVenues, serial]
  )

  const filteredVenues = useMemo(() => {
    if (!search) return scopedVenues
    const q = search.toLowerCase()
    return scopedVenues.filter((v: any) => {
      const name = (v?.alias ?? v?.name ?? '').toLowerCase()
      return name.includes(q)
    })
  }, [scopedVenues, search])
  const label = currentVenue
    ? (currentVenue.alias ?? currentVenue.name ?? 'Venue')
    : 'All venues'

  // On mobile the venue pill is space-constrained, so show just the venue's
  // initials (first letter of each word, e.g. "12 Summerside Place" → "1SP").
  // Falls back to the full label when there's no venue selected ("All venues").
  const mobileLabel = currentVenue
    ? label
        .split(/\s+/)
        .map((word) => word.match(/[a-z0-9]/i)?.[0] ?? '')
        .join('')
        .slice(0, 4)
        .toUpperCase() || label
    : label

  // Disable the switcher on org-only pages — those whose matched nav leaf has
  // no `venueTo` (no per-venue equivalent), e.g. Reports → Receipts/Tags, or
  // fully org-only sections (marketing, inbox, chat). Picking a venue there
  // would strand the user on a route that doesn't exist per-venue. Items that
  // *gate* to a venue (Venue Snapshot etc.) keep a `venueTo`, so the switcher
  // stays enabled and the user can pick. Leaf-based so a venue-capable section
  // (Reports) can still disable on its org-only children.
  const currentSection = findSectionForPath(pathname, { orgId, serial })
  const matchedChain = findItemChainForPath(pathname, { orgId, serial })
  const matchedLeaf = matchedChain[matchedChain.length - 1]
  const isOrgOnlySection = matchedLeaf
    ? !matchedLeaf.venueTo
    : Boolean(currentSection) && !currentSection?.venueTo

  function navigateToVenue(targetSerial: string) {
    // Already inside /venues/:serial/...? Just swap the serial, preserving
    // whatever sub-path (section, sub-page) the user is on.
    const segments = [...pathSegments]
    const venuesIdx = segments.indexOf('venues')
    const hasSerial = venuesIdx >= 0 && segments.length > venuesIdx + 1
    if (hasSerial) {
      segments[venuesIdx + 1] = targetSerial
      navigate(`/${segments.join('/')}`, { viewTransition: true })
      setOpen(false)
      return
    }
    // Not in the venues tree — the user is in an org-level section (e.g.
    // /orgId/square/mappings). Walk the matched chain deepest→shallowest and
    // navigate to the deepest item that has a venue equivalent: a sub-page
    // (/venues/:serial/square/mappings) when it exists, otherwise the section
    // root (/venues/:serial/square) — so the user stays in the same section's
    // sub-nav and never drops back to the venue overview / main nav.
    // Match the chain against the CURRENT context (the page we're actually on)
    // — matching with the target serial resolves every href to its venue form,
    // so nothing matches the org pathname and we'd fall back to the venue
    // overview. Resolve the matched items with the target serial afterwards.
    const chain = findItemChainForPath(pathname, { orgId, serial })
    for (let i = chain.length - 1; i >= 0; i -= 1) {
      const href = resolveHref(chain[i], { orgId, serial: targetSerial })
      if (href?.includes('/venues/')) {
        navigate(href, { viewTransition: true })
        setOpen(false)
        return
      }
    }
    navigate(`/${orgId}/venues/${targetSerial}`, { viewTransition: true })
    setOpen(false)
  }

  function navigateToAll() {
    // Map the current page to its org-level equivalent by walking the matched
    // nav chain deepest→shallowest and using the first item's org href — the
    // same place that item points to at org level (a real org page or the
    // venue-picker gate for venue-only pages). This avoids landing on a missing
    // org route when leaving a venue-only page (e.g. CSV imports).
    const chain = findItemChainForPath(pathname, { orgId, serial })
    for (let i = chain.length - 1; i >= 0; i -= 1) {
      const href = resolveHref(chain[i], { orgId })
      if (href) {
        navigate(href, { viewTransition: true })
        setOpen(false)
        return
      }
    }
    // Fallback: strip /venues/:serial from the path in place.
    const segments = [...pathSegments]
    const venuesIdx = segments.indexOf('venues')
    if (venuesIdx >= 0 && segments.length > venuesIdx + 1) {
      segments.splice(venuesIdx, 2)
    }
    const onlyOrgLeft = segments.length <= 1
    const next = onlyOrgLeft ? `/${orgId}/venues` : `/${segments.join('/')}`
    navigate(next, { viewTransition: true })
    setOpen(false)
  }

  // Org-only sections (marketing, inbox, reports → receipts…) have no
  // per-venue route, so there's nothing to switch — hide the picker entirely
  // rather than showing a disabled pill.
  if (isOrgOnlySection) {
    return null
  }

  return (
    <>
      {squareIntegrations.map((i: any) => (
        <SquareLocationsFetcher
          key={i.id}
          integrationId={i.id}
          skip={!isSquareContext}
          onLoad={reportSquareLocations}
        />
      ))}
      <div className="group/venueswitcher flex min-w-0 items-center gap-1">
        <Combobox open={open} onOpenChange={setOpen}>
          <ComboboxTrigger className="font-medium">
            {initialsOnMobile ? (
              <>
                <span className="truncate md:hidden">{mobileLabel}</span>
                <span className="hidden min-w-0 max-w-[160px] truncate md:inline">
                  {label}
                </span>
              </>
            ) : (
              <span className="min-w-0 max-w-[160px] truncate">{label}</span>
            )}
            <SwitcherChevron />
          </ComboboxTrigger>
          <ComboboxContent className={newUi.enabled ? undefined : 'old-ui-nav'}>
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Find venue..."
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                <CommandEmpty>No venue found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem value="__all__" onSelect={navigateToAll}>
                    <Store className="size-4" />
                    <span>All venues</span>
                    {!serial ? <Check className="ml-auto size-4" /> : null}
                  </CommandItem>
                </CommandGroup>
                {filteredVenues.length > 0 ? (
                  <CommandGroup>
                    {filteredVenues.map((venue: any) => {
                      const vSerial = venue?.serial
                      const vLabel = venue?.alias ?? venue?.name ?? vSerial
                      const isCurrent = vSerial === serial
                      return (
                        <CommandItem
                          key={vSerial}
                          value={vSerial}
                          onSelect={() => {
                            navigateToVenue(vSerial)
                          }}
                        >
                          <VenueInitial name={vLabel} />
                          <span className="truncate">{vLabel}</span>
                          {isCurrent ? (
                            <Check className="ml-auto size-4" />
                          ) : null}
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                ) : null}
              </CommandList>
            </Command>
          </ComboboxContent>
        </Combobox>
        {/* Hover-to-reveal divider + close pill is desktop-only — there's no
        hover on touch devices, and the affordance was eating taps that
        operators meant for the combobox itself. On mobile the venue
        switcher inside the open mobile nav is the path back to "all
        venues". */}
        {serial ? (
          <div className="hidden md:contents">
            <Separator
              orientation="vertical"
              className="mx-1 !h-4 self-center opacity-0 transition-opacity group-hover/venueswitcher:opacity-100"
            />
            <Tooltip>
              <TooltipTrigger
                type="button"
                onClick={navigateToAll}
                aria-label="Back to all venues"
                className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[var(--muted-foreground)] opacity-0 transition-opacity hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] group-hover/venueswitcher:opacity-100"
              >
                <X className="size-4" strokeWidth={2.25} />
              </TooltipTrigger>
              <TooltipContent>Back to all venues</TooltipContent>
            </Tooltip>
          </div>
        ) : null}
      </div>
    </>
  )
}
